import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import RadioButton from "../../components/Atomos/Radio/RadioButton";
import {
  EventHostSponsorEnum,
  EventHostSponsorType,
} from "../../Enums/EventEnums";
import DDMultiple, {
  Option,
} from "../../components/Atomos/DropDown/DDMultiple";
import { firmType, UseFirmList } from "../../hooks/Catalogs/useFirm";
import { useEventForm } from "../../hooks/Events/useEventForm";
import { UseGroupList } from "../../hooks/Catalogs/useGroupList";
import { UsePracticeIndustryList } from "../../hooks/Catalogs/usePracticeIndustry";
import PlusIcon from "../../components/Atomos/Icons/PlusIcon";
import { useEventValidation } from "../../hooks/Events/useEventValidation";
import { IGroup } from "../../interfaces/models/IGroup";
import { GROUP_TYPES } from "../../Enums/GroupEnum";
import { MembershipStatus } from "../../Enums/FirmEnum";

interface HostAndSponsorsProps {
  validation?: ReturnType<typeof useEventValidation>;
}

const HostAndSponsors = ({ validation }: HostAndSponsorsProps) => {
  const { event, updateEventField } = useEventForm();
  const localValidation = useEventValidation();
  const validationHook = validation || localValidation;
  const availableHostSponsorTypes = Object.entries(EventHostSponsorEnum) as [
    EventHostSponsorType,
    string,
  ][];
  const { firmData, isFetching: firmsLoading, refetchFirms } =
    UseFirmList(firmType.both);

  useEffect(() => {
    void refetchFirms();
  }, [refetchFirms]);

  const firmList: Option[] = useMemo(
    () =>
      firmData
        .filter(
          (f: {
            isMember?: boolean;
            membershipStatus?: string;
            shouldHideInStats?: boolean;
          }) =>
            f.shouldHideInStats === true ||
            (f.isMember === true &&
              f.membershipStatus === MembershipStatus.Member)
        )
        .map((f: { id: string; name?: string }) => ({
          id: f.id,
          name: f.name ?? "",
        }))
        .filter(
          (f: { id: string; name: string }) =>
            f.id != null &&
            f.name != null &&
            f.name !== undefined &&
            f.name !== ""
        ),
    [firmData]
  );
  const { groupData } = UseGroupList();
  
  const filterUniqueGroupsByName = useCallback((groups: IGroup[]): IGroup[] => {
    const seen = new Set<string>();
    const uniqueGroups = groups.filter((group) => {
      const title = group.title?.trim().toLowerCase() || "";
      if (seen.has(title)) {
        return false;
      }
      seen.add(title);
      return true;
    });
    
    return uniqueGroups.sort((a, b) => {
      const titleA = (a.title || "").toLowerCase();
      const titleB = (b.title || "").toLowerCase();
      return titleA.localeCompare(titleB);
    });
  }, []);

  const groupList: Option[] = useMemo(
    () => {
      const industryGroups = groupData.filter((g: IGroup) => g.type === GROUP_TYPES.INDUSTRY || g.type === GROUP_TYPES.PRACTICE);
      const filteredGroups = filterUniqueGroupsByName(industryGroups);
      return filteredGroups.map((g: any) => ({ id: g.id, name: g.title }));
    },
    [groupData, filterUniqueGroupsByName]
  );
  const { practiceIndustryData } = UsePracticeIndustryList();
  const practiceIndustryList: Option[] = useMemo(
    () => practiceIndustryData.map((p: any) => ({ id: p.id, name: p.name })),
    [practiceIndustryData]
  );

  const [memberSponsors, setMemberSponsors] = useState<Option[]>([]);
  const [wsgGroups, setWsgGroups] = useState<Option[]>([]);
  const [guestSponsors, setGuestSponsors] = useState<Option[]>([]);
  const [industries, setIndustries] = useState<Option[]>([]);

  const isUpdatingFromRedux = useRef(false);

  const mapToOptions = useCallback(
    (ids: string[], source: Option[], fallbackPrefix: string): Option[] => {
      return ids.map(
        (id) =>
          source.find((o) => o.id === id) ||
          ({ id, name: `${fallbackPrefix} ${id}` } as Option)
      );
    },
    []
  );

  const toSponsorHosts = useCallback(
    (
      items: Option[],
      type: "member" | "wsg_group" | "guest sponsor" | "industry"
    ) => {
      return items
        .filter((i) => i.id)
        .map((i) => ({ type, id: i.id as string }));
    },
    []
  );

  useEffect(() => {
    if (isUpdatingFromRedux.current) {
      isUpdatingFromRedux.current = false;
      return;
    }

    const sponsors = event?.sponsorHost || [];

    const newMemberSponsors = mapToOptions(
      sponsors.filter((s) => s.type === "member").map((s) => s.id),
      firmList,
      "Member"
    );
    const newWsgGroups = mapToOptions(
      sponsors.filter((s) => s.type === "wsg_group").map((s) => s.id),
      groupList,
      "Group"
    );
    const newGuestSponsors = mapToOptions(
      sponsors.filter((s) => s.type === "guest sponsor").map((s) => s.id),
      [],
      "Guest"
    );
    const newIndustries = mapToOptions(
      sponsors.filter((s) => s.type === "industry").map((s) => s.id),
      practiceIndustryList,
      "Industry"
    );

    setMemberSponsors((prev) => {
      if (JSON.stringify(prev) !== JSON.stringify(newMemberSponsors)) {
        return newMemberSponsors;
      }
      return prev;
    });
    setWsgGroups((prev) => {
      if (JSON.stringify(prev) !== JSON.stringify(newWsgGroups)) {
        return newWsgGroups;
      }
      return prev;
    });
    setGuestSponsors((prev) => {
      if (JSON.stringify(prev) !== JSON.stringify(newGuestSponsors)) {
        return newGuestSponsors;
      }
      return prev;
    });
    setIndustries((prev) => {
      if (JSON.stringify(prev) !== JSON.stringify(newIndustries)) {
        return newIndustries;
      }
      return prev;
    });
  }, [
    event?.sponsorHost,
    firmList,
    groupList,
    practiceIndustryList,
    mapToOptions,
  ]);

  useEffect(() => {
    const combined = [
      ...toSponsorHosts(memberSponsors, "member"),
      ...toSponsorHosts(wsgGroups, "wsg_group"),
      ...toSponsorHosts(guestSponsors, "guest sponsor"),
      ...toSponsorHosts(industries, "industry"),
    ];

    const currentSponsors = event?.sponsorHost || [];
    const currentIds = currentSponsors
      .map((s) => `${s.type}:${s.id}`)
      .sort()
      .join(",");
    const newIds = combined
      .map((s) => `${s.type}:${s.id}`)
      .sort()
      .join(",");

    if (currentIds !== newIds) {
      isUpdatingFromRedux.current = true;
      updateEventField("sponsorHost", combined as any);
      if (validationHook.hasAttemptedSave) {
        const updatedEvent = { ...event, sponsorHost: combined };
        validationHook.validateField("sponsorHost", combined, updatedEvent);
      }
    }
  }, [
    memberSponsors,
    wsgGroups,
    guestSponsors,
    industries,
    updateEventField,
    event?.sponsorHost,
    toSponsorHosts,
    validationHook,
    event,
  ]);

  const handleEventHostSponsorChange = useCallback(
    (key: EventHostSponsorType) => {
      updateEventField("eventHostSponsor", key);
      if (validationHook.hasAttemptedSave) {
        const updatedEvent = { ...event, eventHostSponsor: key };
        validationHook.validateField("eventHostSponsor", key, updatedEvent);
        validationHook.validateField(
          "sponsorHost",
          event.sponsorHost,
          updatedEvent
        );
      }
    },
    [updateEventField, validationHook, event]
  );

  const getFieldError = useCallback(
    (fieldName: string) => {
      if (!validationHook.hasAttemptedSave) {
        return undefined;
      }
      return validationHook.errors[fieldName];
    },
    [validationHook.hasAttemptedSave, validationHook.errors]
  );

  const hasFieldError = useCallback(
    (fieldName: string) => {
      return (
        validationHook.hasAttemptedSave && !!validationHook.errors[fieldName]
      );
    },
    [validationHook.hasAttemptedSave, validationHook.errors]
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-12 gap-4">
        {availableHostSponsorTypes.map(([key, value], index) => (
          <div className="col-span-12 md:col-span-6 lg:col-span-3">
            <RadioButton
              key={key}
              index={index}
              text={value}
              selected={event.eventHostSponsor === key}
              handleRadioButtonChange={() => handleEventHostSponsorChange(key)}
              fontSize="!text-base md:!text-lg"
            />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-12 gap-4 md:gap-6 lg:gap-8">
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <DDMultiple
            key={`member-sponsors-${firmList.length}-${firmsLoading ? "loading" : "ready"}`}
            title="Member Sponsors"
            required={["member", "wsgMember", "groupMember"].includes(
              event.eventHostSponsor
            )}
            selectedObjst={memberSponsors}
            seter={setMemberSponsors}
            objects={firmList}
            internalSearch
            placeholder={
              firmsLoading && firmList.length === 0
                ? "Loading firms..."
                : undefined
            }
            tooltip=""
            disabled={
              !["member", "wsgMember", "groupMember"].includes(
                event.eventHostSponsor
              )
            }
            error={
              hasFieldError("sponsorHost") &&
              ["member", "wsgMember", "groupMember"].includes(
                event.eventHostSponsor
              )
            }
            errorMessage={
              hasFieldError("sponsorHost") &&
              ["member", "wsgMember", "groupMember"].includes(
                event.eventHostSponsor
              )
                ? getFieldError("sponsorHost")
                : undefined
            }
          />
        </div>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <DDMultiple
            title="WSG Group"
            required={["group", "groupMember"].includes(event.eventHostSponsor)}
            selectedObjst={wsgGroups}
            seter={setWsgGroups}
            objects={groupList}
            disabled={
              !["group", "groupMember"].includes(event.eventHostSponsor)
            }
            error={
              hasFieldError("sponsorHost") &&
              ["group", "groupMember"].includes(event.eventHostSponsor)
            }
            errorMessage={
              hasFieldError("sponsorHost") &&
              ["group", "groupMember"].includes(event.eventHostSponsor)
                ? getFieldError("sponsorHost")
                : undefined
            }
          />
        </div>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <DDMultiple
            title="Guest Sponsor"
            required={["wsgGuest", "guest"].includes(event.eventHostSponsor)}
            selectedObjst={guestSponsors}
            seter={setGuestSponsors}
            objects={[]}
            disabled={!["wsgGuest", "guest"].includes(event.eventHostSponsor)}
            error={
              hasFieldError("sponsorHost") &&
              ["wsgGuest", "guest"].includes(event.eventHostSponsor)
            }
            errorMessage={
              hasFieldError("sponsorHost") &&
              ["wsgGuest", "guest"].includes(event.eventHostSponsor)
                ? getFieldError("sponsorHost")
                : undefined
            }
            rightButton={
              <button
                className={`${["wsgGuest", "guest"].includes(event.eventHostSponsor) ? "bg-secondary outline-secondary" : "outline-gray-300 bg-gray-300"}
                  w-6 md:w-12 h-8 md:h-[3.25rem] p-0 md:p-1 text-white outline outline-2`}
                onClick={() => {}}
                disabled={
                  !["wsgGuest", "guest"].includes(event.eventHostSponsor)
                }
                type="button"
              >
                <PlusIcon />
              </button>
            }
          />
        </div>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <DDMultiple
            title="Industry"
            required={["industry"].includes(event.eventHostSponsor)}
            selectedObjst={industries}
            seter={setIndustries}
            objects={practiceIndustryList}
            disabled={!["industry"].includes(event.eventHostSponsor)}
            error={
              hasFieldError("sponsorHost") &&
              ["industry"].includes(event.eventHostSponsor)
            }
            errorMessage={
              hasFieldError("sponsorHost") &&
              ["industry"].includes(event.eventHostSponsor)
                ? getFieldError("sponsorHost")
                : undefined
            }
          />
        </div>
      </div>
    </div>
  );
};

export default HostAndSponsors;
